import { ArrowPathIcon, BookOpenIcon, EyeIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';

import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { PublicationPopover } from '@/components/itemsList/action-column/PublicationPopover';
import { ReviewPopover } from '@/components/itemsList/action-column/ReviewPopover';
import { ValidationsPopover } from '@/components/itemsList/action-column/ValidationsPopover';
import { validationStatus } from '@/components/itemsList/action-column/validationStatus';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';

export { PublicationPopover } from '@/components/itemsList/action-column/PublicationPopover';

const ActionsInner = ({
    row,
    isCatalogTable,
    redirectToItemDetail,
    showEditor,
    deleteItem,
    showItemReview,
    items
}: {
    row: any;
    isCatalogTable: boolean;
    redirectToItemDetail?: (item: any) => void;
    showEditor?: (row: any, primaryKey__?:string, editorState?: { onShowItemDetails: true }, items?:any) => void,
    deleteItem?: (row: any) => void,
    showItemReview?:(item:any) => void,
    items?: any[],
}) => {
    const item = row.row.original;
    const {t} = useTranslation();
    const { auth } = useAuth();
    const itemValidationStatus = validationStatus(item);

    // Deny privileged actions while auth is loading so viewers never briefly see enabled controls.
    const canEdit = !!auth?.hasItemPermission('edit', item);
    const canReadOnly = !!auth?.hasItemPermission('read', item) && !canEdit;
    const canRead = canEdit || canReadOnly;
    const canDeleteItem = !!auth?.hasItemPermission('delete', item);
    const canDeleteItemsUi = !!auth?.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'delete.items');

    if (isCatalogTable && redirectToItemDetail) {
        return (
            <div id={`row${row?.id}-actions`} className='tw-flex tw-justify-end'>
                <span
                    onClick={()=>redirectToItemDetail(item)}
                >
                    <IconButton icon={MagnifyingGlassIcon} size="md" className="tw-icon-primary" title={t('ITEMS_LIST_ACTION_VIEW_PRODUCT')}/>
                </span>
            </div>
        );
    } else {
        return (
            <div id={`row${row.id}-actions`} className="tw-flex tw-h-10 tw-max-h-10 tw-items-center tw-space-x-1 tw-justify-end">
                {/* publication */}
                {item.publishedAt__ && <PublicationPopover item={item}/> }

                {/* item has a review */}
                {item.reviewCountTotal__ > 0 && (
                    <ReviewPopover item={item} showItemReview={showItemReview} />
                )}

                {/* LAX-10034: exclusive validation states (legacy hitlist parity) */}
                {itemValidationStatus === 'dirty' && (
                    <div className="tw-flex tw-items-center tw-justify-center tw-p-1">
                        <Icon icon={ArrowPathIcon} size="sm" variant="lighter-gray" className="tw-animate-spin" />
                    </div>
                )}
                {itemValidationStatus === 'non-compliant' && <ValidationsPopover item={item} />}

                {/* item details */}
                {showEditor && <IconButton icon={canEdit ? BookOpenIcon : EyeIcon} size="md" className="tw-icon-primary" disabled={!canRead} onClick={()=> {
                    showEditor(item, undefined, {
                        onShowItemDetails: true
                    }, items);
                }} title={t('VIEW_ITEM')}/>}

                {/* item editor */}
                {showEditor && <IconButton icon={canEdit ? PencilIcon : MagnifyingGlassIcon} size="md" className="tw-icon-primary" disabled={!canRead} onClick={()=> {
                    showEditor(item, undefined, undefined, items);}} title={canEdit ? t('EDIT_ITEM') : t('ITEM.DETAIL_PAGE')}/>}

                {/* delete item — hidden without UI right delete.items; disabled without item delete */}
                {deleteItem && canDeleteItemsUi && <IconButton icon={TrashIcon} size="md" className="tw-icon-primary" disabled={!canDeleteItem} onClick={()=> {
                    deleteItem(item);}} title={t('ITEMS_LIST_ACTION_DELTE_PRODUCT')}/>}
            </div>
        );}
};

export const Actions = memo(ActionsInner, (prev, next) => {
    return (
        prev.row.original === next.row.original &&
        prev.items === next.items &&
        prev.isCatalogTable === next.isCatalogTable &&
        prev.redirectToItemDetail === next.redirectToItemDetail &&
        prev.showEditor === next.showEditor &&
        prev.deleteItem === next.deleteItem &&
        prev.showItemReview === next.showItemReview
    );
});
Actions.displayName = 'Actions';
